import { useMemo, useState } from 'react';
import type { TrendView } from './shared.js';
import {
  availableMonths,
  availableQuarters,
  jstCurrentMonth,
  monthLabelOf,
  quarterLabelOf,
} from './periods.js';

export interface PeriodSelectProps {
  /** 現在の view (月 / Q)。月 view は月一覧、Q view は Q 一覧を出す */
  view: TrendView;
  /** 選択中の月 (月 view) / Q 代表月 (Q view) */
  value: string;
  /** 選択変更。月 view は月、Q view は Q 代表月を返す */
  onChange: (next: string) => void;
}

interface Option {
  value: string;
  label: string;
}

/**
 * 履歴回溯用の期間ドロップダウン。チャート右上 (旧「順調」バッジ位置) に置く。
 * 月 view では過去の月を、Q view では過去の Q を選んで当該期間に回溯できる。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI 折線/バレットの対象期間 (月 or Q) を切替える履歴回溯ドロップダウン
 * @graph-connects none
 */
export function PeriodSelect({ view, value, onChange }: PeriodSelectProps) {
  const [open, setOpen] = useState(false);
  const current = jstCurrentMonth();

  const options = useMemo<Option[]>(() => {
    if (view === 'quarter') {
      return availableQuarters(current).map((q) => ({
        value: q.representativeMonth,
        label: q.label,
      }));
    }
    return availableMonths(current).map((m) => ({
      value: m,
      label: m === current ? `${monthLabelOf(m)}（今月）` : monthLabelOf(m),
    }));
  }, [view, current]);

  const currentLabel =
    options.find((o) => o.value === value)?.label ??
    (view === 'quarter' ? quarterLabelOf(value) : monthLabelOf(value));

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2.5 py-1 text-xs font-semibold text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)] transition-colors"
      >
        {currentLabel}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setOpen(false);
            }}
            aria-hidden="true"
          />
          <ul
            role="listbox"
            /* 選択肢は最大 6 行で固定し、それ以上はスクロール。
               1 行 = py-1.5 (12px) + text-xs line-height (16px) = 28px、6 行 + ul py-1 (上下 8px)。 */
            className="absolute right-0 z-20 mt-1 max-h-[176px] w-40 overflow-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] py-1 shadow-lg"
          >
            {options.map((o) => {
              const selected = o.value === value;
              return (
                <li key={o.value} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                    className={`block w-full px-3 py-1.5 text-left text-xs transition-colors ${
                      selected
                        ? 'bg-[var(--color-bg-hover)] text-[var(--color-text-primary)] font-semibold'
                        : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                    }`}
                  >
                    {o.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
